import React, { useState, useEffect, useMemo, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  User,
  Mail,
  Phone,
  MapPin,
  Bell,
  Shield,
  LogOut,
  ChevronRight,
  Edit2,
  Camera,
  Check,
  Loader2,
} from 'lucide-react';
import {
  Card,
  Button,
  ShimmerButton,
  Input,
  Switch,
  Avatar,
  ResponsiveDialog,
  useToast,
  useViewport,
  cn,
  formatNaira,
  staggerChildren,
  fadeInUp,
} from '@raffle/ui';
import { useAuth } from '../providers/AuthProvider';
import { useNavigate } from 'react-router-dom';
import { useTicketsStore } from '../stores/ticketsStore';
import { api } from '../lib/api';

export const ProfilePage: React.FC = () => {
  const { isDesktop } = useViewport();
  const { user, logout, isAuthenticated, updateUser } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const { tickets, fetchTickets } = useTicketsStore();

  const fullName = user ? `${user.firstName} ${user.lastName}`.trim() : '';
  const avatarInputRef = useRef<HTMLInputElement>(null);

  const [showEditModal, setShowEditModal] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);

  // Fetch user tickets when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchTickets();
    }
  }, [isAuthenticated, fetchTickets]);

  // Calculate stats from real ticket data
  const stats = useMemo(() => {
    if (!isAuthenticated || !tickets.length) {
      return {
        totalTickets: 0,
        activeRaffles: 0,
        totalWon: 0,
        totalSpent: 0,
      };
    }

    const activeRaffles = tickets.filter(t => t.status === 'active').length > 0
      ? new Set(tickets.filter(t => t.status === 'active').map(t => t.propertyId)).size
      : 0;
    const wonTickets = tickets.filter(t => t.status === 'won').length;
    const totalSpent = tickets.reduce((sum, t) => sum + (t.property?.ticketPrice || 0), 0);

    return {
      totalTickets: tickets.length,
      activeRaffles,
      totalWon: wonTickets,
      totalSpent,
    };
  }, [isAuthenticated, tickets]);

  // Edit form state
  const [editForm, setEditForm] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    phone: user?.phone || '',
  });

  // Notification settings
  const [notifications, setNotifications] = useState({
    email: true,
    push: true,
    drawReminders: true,
    promotions: false,
  });

  /** Resize an image file to maxDim×maxDim JPEG and return a base64 data URL */
  const resizeImage = (file: File, maxDim = 256): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let w = img.width;
          let h = img.height;
          if (w > h) { h = Math.round((h / w) * maxDim); w = maxDim; }
          else { w = Math.round((w / h) * maxDim); h = maxDim; }
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d')!;
          ctx.drawImage(img, 0, 0, w, h);
          resolve(canvas.toDataURL('image/jpeg', 0.8));
        };
        img.onerror = reject;
        img.src = reader.result as string;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleAvatarPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Invalid File', 'Please select an image file');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File Too Large', 'Image must be under 10 MB');
      return;
    }
    setIsUploadingAvatar(true);
    try {
      const dataUrl = await resizeImage(file);
      const result = await api.auth.updateMe({ avatarUrl: dataUrl });
      if (result.success && result.data) {
        updateUser(result.data);
        toast.success('Avatar Updated', 'Your profile picture has been updated');
      } else {
        toast.error('Upload Failed', result.error?.message || 'Could not update avatar');
      }
    } catch {
      toast.error('Error', 'Failed to process image');
    } finally {
      setIsUploadingAvatar(false);
      // Reset input so same file can be re-selected
      if (avatarInputRef.current) avatarInputRef.current.value = '';
    }
  };

  const handleSaveProfile = async () => {
    if (!editForm.firstName.trim() || !editForm.lastName.trim()) {
      toast.error('Missing Info', 'First name and last name are required.');
      return;
    }
    setIsSaving(true);
    try {
      const result = await api.auth.updateMe({
        firstName: editForm.firstName.trim(),
        lastName: editForm.lastName.trim(),
        phone: editForm.phone.trim() || null,
      });
      if (result.success && result.data) {
        updateUser(result.data);
        toast.success('Profile Updated', 'Your profile has been updated successfully');
        setShowEditModal(false);
      } else {
        toast.error('Update Failed', result.error?.message || 'Could not update profile');
      }
    } catch {
      toast.error('Network Error', 'Failed to reach the server. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.info('Signed Out', 'You have been signed out successfully');
  };

  return (
    <div className="page-container">
      <section className="content-wrapper py-8">
        {/* Profile Header */}
        <motion.div
          className="relative mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="overflow-hidden">
            {/* Banner */}
            <div className="h-24 md:h-32 bg-gradient-to-r from-gold-600/20 via-gold-500/10 to-gold-600/20" />

            {/* Profile Info */}
            <div className="relative px-6 pb-6">
              {/* Avatar */}
              <div className="absolute -top-12 left-6">
                <div className="relative">
                  <Avatar
                    alt={fullName || 'User'}
                    fallback={fullName || 'User'}
                    src={user?.avatarUrl}
                    size="xl"
                    className="ring-4 ring-surface-1"
                  />
                  <input
                    ref={avatarInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleAvatarPick}
                  />
                  <button
                    className="absolute bottom-0 right-0 p-2 rounded-full bg-gold-500 text-black hover:bg-gold-400 transition-colors disabled:opacity-50"
                    onClick={() => avatarInputRef.current?.click()}
                    disabled={isUploadingAvatar}
                  >
                    {isUploadingAvatar ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Camera className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end pt-2 mb-8">
                <Button variant="outline" size="sm" onClick={() => setShowEditModal(true)}>
                  <Edit2 className="w-4 h-4 mr-2" />
                  Edit Profile
                </Button>
              </div>

              {/* Info */}
              <div>
                <h1 className="font-display text-2xl font-bold text-text-primary mb-1">
                  {fullName || 'Guest User'}
                </h1>
                <p className="text-text-muted">Member since January 2024</p>
              </div>

              {/* Quick Stats */}
              <div className={cn(
                'grid gap-4 mt-6 pt-6 border-t border-white/5',
                isDesktop ? 'grid-cols-4' : 'grid-cols-2'
              )}>
                {[
                  { label: 'Total Tickets', value: stats.totalTickets },
                  { label: 'Active Raffles', value: stats.activeRaffles },
                  { label: 'Wins', value: stats.totalWon },
                  { label: 'Total Spent', value: formatNaira(stats.totalSpent) },
                ].map((stat, index) => (
                  <div key={index} className="text-center">
                    <p className="font-mono text-xl md:text-2xl font-bold text-text-primary tabular-nums">
                      {stat.value}
                    </p>
                    <p className="text-xs text-text-muted">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        </motion.div>

        {/* Settings Grid */}
        <motion.div
          className={cn('grid gap-6', isDesktop ? 'grid-cols-2' : 'grid-cols-1')}
          variants={staggerChildren}
          initial="hidden"
          animate="show"
        >
          {/* Account Info */}
          <motion.div variants={fadeInUp}>
            <Card>
              <div className="p-6">
                <h2 className="font-display text-lg font-semibold text-text-primary mb-6 flex items-center gap-2">
                  <User className="w-5 h-5 text-gold-500" />
                  Account Information
                </h2>

                <div className="space-y-4">
                  <div className="flex items-center justify-between py-3 border-b border-white/5">
                    <div className="flex items-center gap-3">
                      <Mail className="w-4 h-4 text-text-muted" />
                      <div>
                        <p className="text-sm text-text-muted">Email</p>
                        <p className="text-text-primary">{user?.email || 'Not set'}</p>
                      </div>
                    </div>
                    <Check className="w-4 h-4 text-green-400" />
                  </div>

                  <div className="flex items-center justify-between py-3 border-b border-white/5">
                    <div className="flex items-center gap-3">
                      <Phone className="w-4 h-4 text-text-muted" />
                      <div>
                        <p className="text-sm text-text-muted">Phone</p>
                        <p className="text-text-primary">{user?.phone || 'Not set'}</p>
                      </div>
                    </div>
                    {user?.phone && <Check className="w-4 h-4 text-green-400" />}
                  </div>

                  <div className="flex items-center justify-between py-3">
                    <div className="flex items-center gap-3">
                      <MapPin className="w-4 h-4 text-text-muted" />
                      <div>
                        <p className="text-sm text-text-muted">Location</p>
                        <p className="text-text-primary">Lagos, Nigeria</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>

          {/* Notifications */}
          <motion.div variants={fadeInUp}>
            <Card>
              <div className="p-6">
                <h2 className="font-display text-lg font-semibold text-text-primary mb-6 flex items-center gap-2">
                  <Bell className="w-5 h-5 text-gold-500" />
                  Notifications
                </h2>

                <div className="space-y-4">
                  <div className="flex items-center justify-between py-3 border-b border-white/5">
                    <div>
                      <p className="text-text-primary">Email Notifications</p>
                      <p className="text-sm text-text-muted">Receive updates via email</p>
                    </div>
                    <Switch
                      checked={notifications.email}
                      onChange={(checked) => setNotifications((prev) => ({ ...prev, email: checked }))}
                    />
                  </div>

                  <div className="flex items-center justify-between py-3 border-b border-white/5">
                    <div>
                      <p className="text-text-primary">Push Notifications</p>
                      <p className="text-sm text-text-muted">Get instant updates</p>
                    </div>
                    <Switch
                      checked={notifications.push}
                      onChange={(checked) => setNotifications((prev) => ({ ...prev, push: checked }))}
                    />
                  </div>

                  <div className="flex items-center justify-between py-3 border-b border-white/5">
                    <div>
                      <p className="text-text-primary">Draw Reminders</p>
                      <p className="text-sm text-text-muted">Notify before draws</p>
                    </div>
                    <Switch
                      checked={notifications.drawReminders}
                      onChange={(checked) => setNotifications((prev) => ({ ...prev, drawReminders: checked }))}
                    />
                  </div>

                  <div className="flex items-center justify-between py-3">
                    <div>
                      <p className="text-text-primary">Promotions</p>
                      <p className="text-sm text-text-muted">Special offers & deals</p>
                    </div>
                    <Switch
                      checked={notifications.promotions}
                      onChange={(checked) => setNotifications((prev) => ({ ...prev, promotions: checked }))}
                    />
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>

          {/* Security */}
          <motion.div variants={fadeInUp}>
            <Card>
              <div className="p-6">
                <h2 className="font-display text-lg font-semibold text-text-primary mb-6 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-gold-500" />
                  Security
                </h2>

                <div className="space-y-2">
                  <button className="w-full flex items-center justify-between p-4 rounded-vault bg-surface-2/50 hover:bg-surface-2 border border-white/5 transition-colors">
                    <div className="text-left">
                      <p className="text-text-primary">Change Password</p>
                      <p className="text-sm text-text-muted">Update your password</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-text-muted" />
                  </button>

                  <button className="w-full flex items-center justify-between p-4 rounded-vault bg-surface-2/50 hover:bg-surface-2 border border-white/5 transition-colors">
                    <div className="text-left">
                      <p className="text-text-primary">Two-Factor Authentication</p>
                      <p className="text-sm text-text-muted">Add extra security</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-text-muted" />
                  </button>

                  <button className="w-full flex items-center justify-between p-4 rounded-vault bg-surface-2/50 hover:bg-surface-2 border border-white/5 transition-colors">
                    <div className="text-left">
                      <p className="text-text-primary">Login History</p>
                      <p className="text-sm text-text-muted">View recent activity</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-text-muted" />
                  </button>
                </div>
              </div>
            </Card>
          </motion.div>

          {/* Danger Zone */}
          <motion.div variants={fadeInUp}>
            <Card className="border-vault-error/20">
              <div className="p-6">
                <h2 className="font-display text-lg font-semibold text-text-primary mb-6 flex items-center gap-2">
                  <LogOut className="w-5 h-5 text-vault-error" />
                  Account Actions
                </h2>

                <div className="space-y-3">
                  <Button
                    variant="outline"
                    fullWidth
                    onClick={() => setShowLogoutConfirm(true)}
                    className="justify-start"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign Out
                  </Button>

                  <button className="w-full flex items-center gap-2 p-3 rounded-vault text-vault-error/70 hover:text-vault-error hover:bg-vault-error/10 transition-colors text-sm">
                    Delete Account
                  </button>
                </div>
              </div>
            </Card>
          </motion.div>
        </motion.div>
      </section>

      {/* Edit Profile Modal */}
      <ResponsiveDialog
        isOpen={showEditModal}
        onClose={() => !isSaving && setShowEditModal(false)}
        title="Edit Profile"
      >
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="First Name"
              value={editForm.firstName}
              onChange={(e) => setEditForm((prev) => ({ ...prev, firstName: e.target.value }))}
              placeholder="First name"
            />
            <Input
              label="Last Name"
              value={editForm.lastName}
              onChange={(e) => setEditForm((prev) => ({ ...prev, lastName: e.target.value }))}
              placeholder="Last name"
            />
          </div>

          <Input
            label="Email"
            type="email"
            value={editForm.email}
            disabled
            placeholder="Email cannot be changed"
          />

          <Input
            label="Phone Number"
            type="tel"
            value={editForm.phone}
            onChange={(e) => setEditForm((prev) => ({ ...prev, phone: e.target.value }))}
            placeholder="+234 xxx xxx xxxx"
          />

          <div className="flex gap-3">
            <Button variant="outline" fullWidth onClick={() => setShowEditModal(false)} disabled={isSaving}>
              Cancel
            </Button>
            <ShimmerButton fullWidth onClick={handleSaveProfile} loading={isSaving}>
              Save Changes
            </ShimmerButton>
          </div>
        </div>
      </ResponsiveDialog>

      {/* Logout Confirmation */}
      <ResponsiveDialog
        isOpen={showLogoutConfirm}
        onClose={() => setShowLogoutConfirm(false)}
        title="Sign Out"
        modalSize="sm"
      >
        <div className="p-6 space-y-6">
          <p className="text-text-secondary text-center">
            Are you sure you want to sign out of your account?
          </p>

          <div className="flex gap-3">
            <Button variant="outline" fullWidth onClick={() => setShowLogoutConfirm(false)}>
              Cancel
            </Button>
            <Button variant="danger" fullWidth onClick={handleLogout}>
              Sign Out
            </Button>
          </div>
        </div>
      </ResponsiveDialog>
    </div>
  );
};
